// src/components/PWANotificationPrompt.js
// Modale minimaliste pour demander l'autorisation des notifications Push PWA
// ✅ PHASE 3: Integration Push Notifications (web-push)
import { useEffect, useState } from 'react'
import CIcon from '@coreui/icons-react'
import { cilBell, cilX, cilSync } from '@coreui/icons'
import notificationService from '../services/notificationService'
import {
  isPushSupported,
  isPWAMode,
  getPermissionStatus,
  subscribePush,
  isPushEnabled,
} from '../services/pushSubscriptionService'
import './PWANotificationPrompt.css'

const PWANotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const checkAndShowPrompt = async () => {
      // Security: userId in localStorage is for UI state only - auth handled via httpOnly cookies
      const userId = localStorage.getItem('userId')
      if (!userId) return

      // Verifier si on est en mode PWA standalone (ou forcer en dev)
      const isPWA = isPWAMode()
      const isDev = process.env.NODE_ENV === 'development'

      // En production, afficher uniquement en mode PWA
      // En dev, toujours permettre pour tester
      if (!isPWA && !isDev) return

      // Verifier si on a deja demande
      const hasAskedBefore = localStorage.getItem('pwa-notification-prompted')
      if (hasAskedBefore) return

      // Verifier si Push est supporte
      if (!isPushSupported()) {
        console.log('[PWA] Push non supporte sur ce navigateur')
        return
      }

      // Verifier si deja active
      const alreadyEnabled = await isPushEnabled()
      if (alreadyEnabled) {
        localStorage.setItem('pwa-notification-prompted', 'true')
        return
      }

      // Si permission deja refusee, ne pas redemander
      const permission = getPermissionStatus()
      if (permission === 'denied') {
        localStorage.setItem('pwa-notification-prompted', 'true')
        return
      }

      // Attendre 2 secondes apres le chargement
      const timer = setTimeout(() => {
        setShowPrompt(true)
      }, 2000)

      return () => clearTimeout(timer)
    }

    checkAndShowPrompt()
  }, [])

  const handleAllow = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Creer la subscription Push (inclut la demande de permission)
      const result = await subscribePush()

      if (result.success) {
        // Activer aussi les notifications browser (son, etc.)
        if (!notificationService.areBrowserNotificationsEnabled()) {
          notificationService.toggleBrowserNotifications()
        }

        console.log('[PWA] Push notifications activees avec succes')
        localStorage.setItem('pwa-notification-prompted', 'true')
        setShowPrompt(false)
      } else {
        // Permission refusee ou erreur
        if (result.error === 'Permission refusee') {
          setError('Vous avez refuse les notifications. Activez-les dans les parametres de votre navigateur.')
        } else {
          setError(result.error || 'Erreur lors de l\'activation')
        }
        console.warn('[PWA] Echec activation push:', result.error)
      }
    } catch (err) {
      console.error('[PWA] Erreur notifications:', err)
      setError('Une erreur est survenue')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDismiss = () => {
    localStorage.setItem('pwa-notification-prompted', 'true')
    setShowPrompt(false)
  }

  if (!showPrompt) return null

  return (
    <div className="notification-prompt-overlay" onClick={handleDismiss}>
      <div className="notification-prompt" onClick={(e) => e.stopPropagation()}>
        <button
          className="notification-prompt-close"
          onClick={handleDismiss}
          aria-label="Fermer"
          disabled={isLoading}
        >
          <CIcon icon={cilX} />
        </button>

        <div className="notification-prompt-icon">
          <CIcon icon={cilBell} />
        </div>

        <div className="notification-prompt-content">
          <p className="notification-prompt-title">Activer les notifications ?</p>
          <p className="notification-prompt-subtitle">
            Pour etre informe des nouveaux messages, meme quand l'app est fermee
          </p>
          {error && <p className="notification-prompt-error">{error}</p>}
        </div>

        <div className="notification-prompt-actions">
          <button
            className="notification-prompt-btn secondary"
            onClick={handleDismiss}
            disabled={isLoading}
          >
            Non merci
          </button>
          <button
            className="notification-prompt-btn primary"
            onClick={handleAllow}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <CIcon icon={cilSync} className="spin" /> Activation...
              </>
            ) : (
              'Activer'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PWANotificationPrompt
